"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EllipticCurvePreset = exports.EllipticCurve = void 0;
exports.getCurve = getCurve;
const p256_1 = require("@noble/curves/p256");
const secp256k1_1 = require("@noble/curves/secp256k1");
const utils_1 = require("@noble/curves/utils");
const SIGNATURE_COMPONENT_HEX_LENGTH = 32 * 2;
function getNobleCurve(preset) {
    switch (preset) {
        case "p256":
            return p256_1.p256;
        case "secp256k1":
            return secp256k1_1.secp256k1;
        default:
            throw new Error(`Unsupported curve preset: ${preset}`);
    }
}
/**
 * Interface for providing elliptic cryptography methods. Instead of instantiating your own curve, you should be getting a curve through getCurve.
 *
 * @example
 *
 * const curve = getCurve(EllipticCurvePreset.SECP256R1);
 * const message = tx.serialize();
 * const signature = curve.sign(message, privateKey);
 */
class EllipticCurve {
    constructor(preset) {
        this.curve = getNobleCurve(preset);
    }
    /**
     * Signs a message with the given private key.
     * @param message - hexstring message.
     * @param privateKey - hexstring.
     * @param _k - deprecated and ignored. Custom nonce signing is not supported.
     */
    sign(message, privateKey, _k) {
        const signature = this.curve.sign((0, utils_1.hexToBytes)(message), privateKey, {
            lowS: true,
        });
        const compactSignature = signature.toHex("compact");
        return {
            r: compactSignature.slice(0, SIGNATURE_COMPONENT_HEX_LENGTH),
            s: compactSignature.slice(SIGNATURE_COMPONENT_HEX_LENGTH),
        };
    }
    /**
     * Validates a signature against the original message and the public key of the signing key.
     * @param message - the original hexstring message.
     * @param signature - the signature.
     * @param publicKey - encoded or unencoded public key.
     */
    verify(message, signature, publicKey) {
        return this.curve.verify((0, utils_1.hexToBytes)(signature.r + signature.s), (0, utils_1.hexToBytes)(message), (0, utils_1.hexToBytes)(publicKey), { lowS: false });
    }
    /**
     * Generates the public key from the given private key. Encoding it results in a shorter public key.
     * @param privateKey - 64 byte hexstring.
     * @param encode - whether to return the compressed form.
     */
    getPublicKey(privateKey, encode = true) {
        return (0, utils_1.bytesToHex)(this.curve.getPublicKey(privateKey, encode));
    }
    /**
     * Decodes an encoded public key to its unencoded form.
     * @param publicKey - 33 byte hexstring starting with 02 or 03.
     */
    decodePublicKey(publicKey) {
        return this.curve.ProjectivePoint.fromHex(publicKey).toHex(false);
    }
}
exports.EllipticCurve = EllipticCurve;
var EllipticCurvePreset;
(function (EllipticCurvePreset) {
    EllipticCurvePreset[EllipticCurvePreset["SECP256R1"] = 0] = "SECP256R1";
    EllipticCurvePreset[EllipticCurvePreset["SECP256K1"] = 1] = "SECP256K1";
})(EllipticCurvePreset || (exports.EllipticCurvePreset = EllipticCurvePreset = {}));
const curves = {
    [EllipticCurvePreset.SECP256R1]: new EllipticCurve("p256"),
    [EllipticCurvePreset.SECP256K1]: new EllipticCurve("secp256k1"),
};
function getCurve(curveName) {
    const curve = curves[curveName];
    if (curve === undefined) {
        throw new Error(`Unsupported curve preset: ${curveName}`);
    }
    return curve;
}
